import { get } from "lodash";
import { useFormContext } from "react-hook-form";
import { z } from "zod";

import { useGetShouldUpdateField } from "~/hooks/useShouldUpdateField";

export type SaveFormFormProviderProps = Record<string, unknown>;

export const baseSchema = z.object({
  id: z.string(),
  description: z.string().optional(),
  title: z.string().optional(),

  space: z
    .object({
      id: z.string(),
      collections: z.array(z.string()).optional().default([]),
      tags: z.array(z.string()).optional().default([]),
    })
    .required(),

  type: z.enum(["URL", "MEDIA", "FILE", "NOTE"]).optional(),
  intent: z.enum(["create", "update"]),
});

export const urlSchema = baseSchema.merge(
  z.object({
    step: z.enum(["addUrl", "editUrl", "form", "selectLocation"]).optional(),
    type: z.literal("URL"),
    url: z.string().url(),
    urlMeta: z
      .object({
        title: z.string(),
        description: z.string().optional(),
        image: z.string().optional(),
        logo: z.string().optional(),
      })
      .optional(),
  }),
);
export const mediaSchema = baseSchema.merge(
  z.object({
    step: z.enum(["showAsset", "form", "selectLocation"]).optional(),
    type: z.literal("MEDIA"),
    r2Key: z.string(),
    mediaMeta: z.object({
      assetId: z.string().optional(),
      width: z.number().optional(),
      height: z.number().optional(),
      type: z.enum(["image", "video"]),
      fileName: z.string().optional(),
      fileSize: z.number().optional(),
      duration: z.number().optional(),
      mimeType: z.string(),
      uri: z.string().optional(),
    }),
  }),
);

export const fileSchema = baseSchema.merge(
  z.object({
    step: z.enum(["showAsset", "form", "selectLocation"]).optional(),
    type: z.literal("FILE"),
    r2Key: z.string(),
    fileMeta: z.object({
      name: z.string().optional(),
      size: z.number().optional(),
      mimeType: z.string().optional(),
      uri: z.string().optional(),
    }),
  }),
);

export const noteSchema = baseSchema.merge(
  z.object({
    step: z.enum(["takeNote", "form", "selectLocation"]).optional(),
    type: z.literal("NOTE"),
    richTextAST: z.record(z.any()),
  }),
);

export type SaveFormUrlValues = z.infer<typeof urlSchema>;
export type SaveFormFileValues = z.infer<typeof fileSchema>;
export type SaveFormMediaValues = z.infer<typeof mediaSchema>;
export type SaveFormNoteValues = z.infer<typeof noteSchema>;

export type SaveFormValues =
  | SaveFormUrlValues
  | SaveFormMediaValues
  | SaveFormFileValues
  | SaveFormNoteValues;

export const useSaveFormContext = () => useFormContext<SaveFormValues>();
export const useGetShouldUpdateFieldForSaveForm = () =>
  useGetShouldUpdateField<SaveFormValues>();

export const stepHasErrors = (
  errors: ReturnType<typeof useSaveFormContext>["formState"]["errors"],
) => {
  return (step: SaveFormValues["step"]) => {
    switch (step) {
      case "addUrl":
        return Boolean(get(errors, "url"));
      case "editUrl":
        return Boolean(get(errors, "url"));
      case "selectLocation":
        return Boolean(get(errors, "location"));
      case "form":
        return get(errors, "title") || get(errors, "description");
      default:
        return false;
    }
  };
};
